import type { ChatMessage } from "@loot-os/api-contract";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@loot-os/ui/components/sheet";
import { useQuery } from "@tanstack/react-query";
import { ChevronsRight, EyeOff, Megaphone, ScrollText } from "lucide-react";
import { useCallback, useState } from "react";

import {
  CHAT_SLIDE_MOTION,
  DEFAULT_CHAT_ROOM,
  setChatDrawerOpen,
  toUserFacingChatError,
  useChatHeartbeat,
  useChatPanel,
  useChatRoom,
  useDeleteChatMessage,
  useIgnoredUsers,
  useIsDesktop,
} from "../../lib/chat.js";
import { lootMeQueryOptions } from "../../lib/queries.js";
import { useShopper } from "../../lib/shopper.js";
import { ChatComposer } from "./ChatComposer.js";
import { ChatMessageList } from "./ChatMessageList.js";
import { ChatRulesDialog } from "./ChatRulesDialog.js";
import { IgnoredUsersDialog } from "./IgnoredUsersDialog.js";
import { ReportChatDialog } from "./ReportChatDialog.js";

/**
 * The storefront's chat (boxed's chat panel, styled from the Mystic7 Figma sidebar): fixed to the
 * right edge of a wide screen under the header, so it stays put while the page scrolls, and
 * collapsible; a drawer on a phone, opened from the header. Rendered only when the store has chat
 * on. It overlays the page; nothing is pushed aside.
 */
export function ChatPanel() {
  const isDesktop = useIsDesktop();
  const { desktopOpen, setDesktopOpen, mobileOpen } = useChatPanel();

  if (isDesktop) {
    // Kept mounted while collapsed and slid off the right edge, in step with the tab
    // (`CHAT_SLIDE_MOTION`); `inert` takes it out of focus and the accessibility tree meanwhile.
    return (
      <aside
        aria-label="Chat"
        inert={!desktopOpen}
        className={`fixed top-14 right-0 bottom-0 z-30 flex w-80 flex-col border-l border-border bg-card text-card-foreground transition-transform ${CHAT_SLIDE_MOTION} ${
          desktopOpen ? "translate-x-0 shadow-xl" : "translate-x-full"
        }`}
      >
        <ChatRoom onClose={() => setDesktopOpen(false)} closeLabel="Collapse chat" />
      </aside>
    );
  }
  return (
    <Sheet open={mobileOpen} onOpenChange={setChatDrawerOpen}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="w-full gap-0 bg-card p-0 text-card-foreground data-[side=right]:w-full data-[side=right]:sm:max-w-sm"
      >
        <SheetTitle className="sr-only">Chat</SheetTitle>
        <SheetDescription className="sr-only">The store's chat room.</SheetDescription>
        {mobileOpen && (
          <ChatRoom onClose={() => setChatDrawerOpen(false)} closeLabel="Close chat" />
        )}
      </SheetContent>
    </Sheet>
  );
}

function ChatRoom({ onClose, closeLabel }: { onClose: () => void; closeLabel: string }) {
  const roomId = DEFAULT_CHAT_ROOM;
  const { user, sessionToken, authPending, sessionPending } = useShopper();
  const room = useChatRoom(roomId);
  const me = useQuery({ ...lootMeQueryOptions(), enabled: !!sessionToken });
  const { isIgnored, ignore, unignore } = useIgnoredUsers();
  const remove = useDeleteChatMessage(roomId);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [reporting, setReporting] = useState<ChatMessage | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [ignoredOpen, setIgnoredOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const signedIn = !!user;
  useChatHeartbeat(roomId, signedIn && !!sessionToken);

  const viewerEndUserId = me.data?.id ?? null;
  const onReply = useCallback((message: ChatMessage) => setReplyingTo(message), []);
  const onReport = useCallback((message: ChatMessage) => setReporting(message), []);
  const onDelete = useCallback(
    (message: ChatMessage) => {
      setActionError(null);
      remove.mutate(message.messageId, {
        onError: (err) => setActionError(toUserFacingChatError(err)),
      });
    },
    [remove.mutate],
  );
  const onToggleIgnore = useCallback(
    (message: ChatMessage) => {
      if (isIgnored(message.endUserId)) unignore(message.endUserId);
      else ignore({ endUserId: message.endUserId, displayName: message.displayName });
    },
    [isIgnored, ignore, unignore],
  );

  const headcount = room.data?.headcount ?? 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The room tabs row (one room today): the active room with its online count, and the
          collapse or close control. */}
      <div className="flex h-9 shrink-0 items-center gap-2 px-2 py-1">
        <div
          className="flex h-7 flex-1 items-center justify-center gap-2 rounded-md border border-border bg-background/40 px-2 text-sm font-medium"
          aria-current="true"
        >
          <Megaphone className="size-4 text-muted-foreground" aria-hidden />
          <span>General</span>
          <span
            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium tabular-nums text-muted-foreground"
            title={`${headcount} online`}
          >
            {headcount}
            <span className="sr-only"> online</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={closeLabel}
        >
          <ChevronsRight className="size-4" />
        </button>
      </div>

      {room.isPending ? (
        <div className="flex-1 px-3 py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : room.isError ? (
        <div className="flex-1 px-3 py-8 text-center text-sm text-destructive">
          {toUserFacingChatError(room.error)}
        </div>
      ) : (
        <ChatMessageList
          messages={room.data.messages}
          viewerEndUserId={viewerEndUserId}
          viewerUsername={me.data?.username ?? null}
          isIgnored={isIgnored}
          onReply={onReply}
          onReport={onReport}
          onDelete={onDelete}
          onToggleIgnore={onToggleIgnore}
        />
      )}

      {actionError && <p className="px-3 pb-1 text-xs text-destructive">{actionError}</p>}
      <div className="flex shrink-0 justify-end gap-1 border-t border-border px-3 pt-3">
        <FieldButton label="Chat rules" onClick={() => setRulesOpen(true)}>
          <ScrollText />
        </FieldButton>
        <FieldButton label="Ignored players" onClick={() => setIgnoredOpen(true)}>
          <EyeOff />
        </FieldButton>
      </div>
      <ChatComposer
        roomId={roomId}
        signedIn={signedIn}
        sessionReady={!authPending && !sessionPending && !!sessionToken && me.isSuccess}
        username={me.data?.username ?? null}
        muted={room.data?.viewer?.muted ?? false}
        mutedUntil={room.data?.viewer?.mutedUntil}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
      />

      <ReportChatDialog message={reporting} onClose={() => setReporting(null)} />
      <ChatRulesDialog open={rulesOpen} onOpenChange={setRulesOpen} />
      <IgnoredUsersDialog open={ignoredOpen} onOpenChange={setIgnoredOpen} />
    </div>
  );
}

/** The small square tool buttons above the message box. */
function FieldButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex size-6 items-center justify-center rounded-md border border-input bg-foreground/5 text-muted-foreground shadow-xs transition-colors hover:text-foreground [&>svg]:size-4"
    >
      {children}
    </button>
  );
}
